import { useState } from 'react';
import { Calculator, TrendingUp, Users, DollarSign } from 'lucide-react';

const CommissionCalculator = () => {
  const [nivel1Count, setNivel1Count] = useState(0);
  const [nivel2Count, setNivel2Count] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState('emprendedor');

  const plans = [
    { id: 'explorador', name: 'Explorador', price: 20000 },
    { id: 'emprendedor', name: 'Emprendedor', price: 50000 },
    { id: 'elite', name: 'Elite', price: 70000 }
  ];

  const calculateCommissions = () => {
    const planPrice = plans?.find(p => p?.id === selectedPlan)?.price || 0;
    const nivel1Commission = nivel1Count * planPrice * 0.20;
    const nivel2Commission = nivel2Count * planPrice * 0.10;
    const totalCommission = nivel1Commission + nivel2Commission;

    return {
      nivel1: nivel1Commission,
      nivel2: nivel2Commission,
      total: totalCommission
    };
  };

  const commissions = calculateCommissions();

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(value);
  };

  return (
    <div className="bg-card rounded-xl shadow-lg border border-border p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-primary/10 rounded-lg">
          <Calculator className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-foreground">Calculadora de Comisiones</h2>
          <p className="text-sm text-muted-foreground">Estima tus ganancias mensuales con Bizznet Rewards</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Nivel 1 Input */}
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Users className="w-4 h-4 text-primary" />
            Referidos Nivel 1 (20% comisión)
          </label>
          <input
            type="number"
            min="0"
            value={nivel1Count}
            onChange={(e) => setNivel1Count(Math.max(0, parseInt(e?.target?.value) || 0))}
            className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
            placeholder="Número de referidos directos"
          />
        </div>

        {/* Nivel 2 Input */}
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-medium text-foreground">
            <TrendingUp className="w-4 h-4 text-secondary" />
            Referidos Nivel 2 (10% comisión)
          </label>
          <input
            type="number"
            min="0"
            value={nivel2Count}
            onChange={(e) => setNivel2Count(Math.max(0, parseInt(e?.target?.value) || 0))}
            className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
            placeholder="Número de referidos indirectos"
          />
        </div>
      </div>
      {/* Plan Selection */}
      <div className="space-y-2 mb-6">
        <label className="flex items-center gap-2 text-sm font-medium text-foreground">
          <DollarSign className="w-4 h-4 text-accent" />
          Plan Promedio de Referidos
        </label>
        <div className="grid grid-cols-3 gap-3">
          {plans?.map((plan) => (
            <button
              key={plan?.id}
              onClick={() => setSelectedPlan(plan?.id)}
              className={`px-4 py-3 rounded-lg border-2 transition-all ${
                selectedPlan === plan?.id
                  ? 'border-primary bg-primary/10 text-primary font-semibold' :'border-border bg-background text-muted-foreground hover:border-primary/50'
              }`}
            >
              <div className="text-sm font-medium">{plan?.name}</div>
              <div className="text-xs mt-1">{formatCurrency(plan?.price)}</div>
            </button>
          ))}
        </div>
      </div>
      {/* Results */}
      <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-lg p-6 space-y-4">
        <h3 className="text-lg font-semibold text-foreground mb-4">Ganancias Mensuales Estimadas</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Nivel 1 Earnings */}
          <div className="bg-card rounded-lg p-4 border border-border">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-primary" />
              <span className="text-sm text-muted-foreground">Nivel 1</span>
            </div>
            <div className="text-2xl font-bold text-primary">
              {formatCurrency(commissions?.nivel1)}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {nivel1Count} referidos × 20%
            </div>
          </div>

          {/* Nivel 2 Earnings */}
          <div className="bg-card rounded-lg p-4 border border-border">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-secondary" />
              <span className="text-sm text-muted-foreground">Nivel 2</span>
            </div>
            <div className="text-2xl font-bold text-secondary">
              {formatCurrency(commissions?.nivel2)}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {nivel2Count} referidos × 10%
            </div>
          </div>

          {/* Total Earnings */}
          <div className="bg-gradient-to-br from-primary to-secondary rounded-lg p-4 border border-primary">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-4 h-4 text-white" />
              <span className="text-sm text-white/90">Total Mensual</span>
            </div>
            <div className="text-2xl font-bold text-white">
              {formatCurrency(commissions?.total)}
            </div>
            <div className="text-xs text-white/80 mt-1">
              Ingreso recurrente
            </div>
          </div>
        </div>

        {/* Info Note */}
        <div className="bg-card rounded-lg p-4 border border-border mt-4">
          <p className="text-xs text-muted-foreground leading-relaxed">
            <span className="font-semibold text-foreground">Cómo se calculan las ganancias:</span><br />
            Nivel 1: 20% de comisión por cada referido directo que se registra.<br />
            Nivel 2: 10% de comisión por cada referido de tus referidos<br /><br />
            <span className="font-semibold text-foreground">Nota:</span> Esta calculadora muestra estimaciones basadas en el programa Bizznet Rewards. 
            Las comisiones reales dependen de que tus referidos mantengan sus planes activos.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CommissionCalculator;